import { useMemo, useState } from "react";
import { useMastersStore } from "../data/mastersStore";
import type { Warehouse } from "../data/schemaTypes";
import { Badge, Button, Card, Input } from "../components/ui";
import { WarehouseEditModal } from "../features/warehouses/WarehouseEditModal";

// Almacenes page — listado con counts derivados (cuántos vehículos y
// choferes operan desde cada almacén) y editor de campos básicos.
export function WarehousesPage() {
  const warehouses = useMastersStore((s) => s.warehouses);
  const vehicles = useMastersStore((s) => s.vehicles);
  const users = useMastersStore((s) => s.users);

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Warehouse | null>(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return warehouses
      .map((w) => {
        const wVehicles = vehicles.filter((v) => v.warehouse_id === w.id);
        const driverIds = new Set(
          wVehicles
            .map((v) => v.current_driver_user_id)
            .filter((id): id is string => !!id),
        );
        const wDrivers = users.filter(
          (u) => u.role === "driver" && driverIds.has(u.id),
        );
        return { warehouse: w, vehicleCount: wVehicles.length, driverCount: wDrivers.length };
      })
      .filter(({ warehouse }) => {
        if (!q) return true;
        return (
          warehouse.code.toLowerCase().includes(q) ||
          warehouse.name.toLowerCase().includes(q) ||
          (warehouse.address ?? "").toLowerCase().includes(q)
        );
      });
  }, [warehouses, vehicles, users, search]);

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-ink">Almacenes</h1>
          <p className="text-xs text-sub mt-0.5">
            {warehouses.length} almacenes ·{" "}
            {warehouses.filter((w) => w.active).length} activos
          </p>
        </div>
        <Button variant="primary" size="sm" disabled>
          + Nuevo almacén
        </Button>
      </header>

      <Card padding="sm" className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <Input
            label="Buscar"
            placeholder="Código, nombre o dirección…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card>

      <div className="bg-card border border-border rounded-lg shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-page text-sub border-b border-border">
            <tr className="text-left">
              <Th>Código</Th>
              <Th>Nombre</Th>
              <Th>Dirección</Th>
              <Th>Coordenadas</Th>
              <Th>Vehículos</Th>
              <Th>Choferes</Th>
              <Th>Estado</Th>
              <th className="px-3 py-2 w-20" />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ warehouse, vehicleCount, driverCount }) => (
              <tr
                key={warehouse.id}
                className="border-b border-border last:border-b-0 hover:bg-page/60"
              >
                <td className="px-3 py-2 font-mono text-xs text-primary">
                  {warehouse.code}
                </td>
                <td className="px-3 py-2 text-ink font-medium">
                  {warehouse.name}
                </td>
                <td className="px-3 py-2 text-sub">{warehouse.address ?? "—"}</td>
                <td className="px-3 py-2 text-sub font-mono text-xs">
                  {warehouse.lat != null && warehouse.lon != null
                    ? `${warehouse.lat.toFixed(4)}, ${warehouse.lon.toFixed(4)}`
                    : "—"}
                </td>
                <td className="px-3 py-2 text-ink">{vehicleCount}</td>
                <td className="px-3 py-2 text-ink">{driverCount}</td>
                <td className="px-3 py-2">
                  <Badge variant={warehouse.active ? "delivered" : "pending"}>
                    {warehouse.active ? "Activo" : "Inactivo"}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setEditing(warehouse)}
                    className="text-xs text-primary hover:underline"
                  >
                    Editar
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sub">
                  No hay almacenes que coincidan con la búsqueda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <WarehouseEditModal warehouse={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wide">
      {children}
    </th>
  );
}
